import { FC } from 'react';
import classNames from 'classnames';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CustomCheckbox } from 'components';
import { DraggableItemIcon, HideEyeIcon, OpenEyeIcon } from 'assets/icons';
import { ColumnConfig } from './CustomizeColumnsModal';

interface SortableItemProps {
  column: ColumnConfig;
  toggleColumnSelection: (columnKey: string) => void;
  canReorder: boolean;
  isFirst: boolean;
  isLast: boolean;
}

const SortableItem: FC<SortableItemProps> = ({ column, toggleColumnSelection, canReorder, isFirst, isLast }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.columnKey,
    disabled: !canReorder
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 'auto',
    width: isDragging ? 'calc(100% - 10px)' : '100%',
    maxWidth: '100%',
    position: isDragging ? 'relative' : undefined,
    overflow: 'hidden'
  } as React.CSSProperties;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={classNames(
        `relative flex py-3 w-full ${
          canReorder ? 'cursor-default' : 'cursor-default bg-[#F8F8F8]'
        } items-center border border-secondaryBtn bg-white px-4 gap-4`,
        isFirst ? 'rounded-t-md ' : '',
        isLast ? 'rounded-b-md' : ''
      )}>
      <CustomCheckbox
        id={`column-checkbox-${column.columnKey}`}
        isChecked={column.isEnabled}
        className="rounded-none border-0 border-transparent bg-transparent p-2"
        width="w-6"
        height="h-6"
        disabled={column.showAlways}
        onClick={() => toggleColumnSelection(column.columnKey)}>
        <div className={`m-2 ${column.showAlways ? 'opacity-50' : ''} group transition-all duration-200`}>
          <div
            className={`${!column.showAlways ? 'transition-all duration-200 hover:scale-110 hover:opacity-80' : ''} `}>
            {column.isEnabled ? <OpenEyeIcon /> : <HideEyeIcon />}
          </div>
        </div>
      </CustomCheckbox>
      <div
        className={`ml-3 flex w-full ${!canReorder ? 'cursor-default' : 'cursor-move'} items-center justify-between`}
        {...(canReorder && column.isEnabled ? attributes : {})}
        {...(canReorder && column.isEnabled ? listeners : {})}>
        <div className={`grow text-sbase font-semibold ${column.isEnabled ? 'text-primaryText' : 'text-accentText'}`}>
          {column.columnLabel}
        </div>
        {canReorder && column.isEnabled ? (
          <div>
            <DraggableItemIcon />
          </div>
        ) : (
          <div className="h-6 w-6"></div>
        )}
      </div>
    </div>
  );
};

export default SortableItem;
